'use client'

import { forwardRef, ReactNode, useState } from 'react'
import { ChevronDown, ChevronUp, MoreVertical, AlertTriangle, Info, CheckCircle2, Clock } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Card Component
 * 
 * Enhanced card implementing all 10 BIRHAUS principles:
 * - Information hierarchy with progressive disclosure
 * - Spanish-first labeling and content
 * - One clear primary action per card
 * - Accessibility compliance (WCAG AA+)
 * - Cognitive load reduction through structured layout
 * - Performance optimized with lazy loading
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausCardAction {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  icon?: ReactNode
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'destructive'
  disabled?: boolean
  loading?: boolean
}

export interface BirhausCardProps extends React.HTMLAttributes<HTMLDivElement> {
  // BIRHAUS Principle #7: Bilingual by design
  title?: string
  titleEs?: string
  titleEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  subtitle?: string
  subtitleEs?: string
  subtitleEn?: string
  
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Content organization
  children?: ReactNode
  header?: ReactNode
  footer?: ReactNode
  aside?: ReactNode
  
  // BIRHAUS Principle #3: One clear action
  primaryAction?: BirhausCardAction
  secondaryActions?: BirhausCardAction[] // Max 2-3 recommended
  
  // BIRHAUS Principle #4: Progressive disclosure
  collapsible?: boolean
  defaultExpanded?: boolean
  expandedContent?: ReactNode
  
  // BIRHAUS Principle #2: Honest data
  status?: 'neutral' | 'success' | 'warning' | 'error' | 'info'
  statusMessage?: string
  statusMessageEs?: string
  statusMessageEn?: string
  
  // Visual properties
  variant?: 'default' | 'elevated' | 'outlined' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  
  // BIRHAUS Principle #8: Performance is UX
  loading?: boolean
  skeleton?: boolean
  lazyLoad?: boolean
  
  // Enhanced functionality
  selectable?: boolean
  selected?: boolean
  onSelect?: () => void
  
  // Badge/indicator
  badge?: string | number
  badgeVariant?: 'neutral' | 'success' | 'warning' | 'error'
  
  // Metadata
  timestamp?: Date
  author?: string
  category?: string
  
  // Cognitive load tracking
  informationDensity?: 'low' | 'medium' | 'high'
  
  // Styling
  className?: string
}

export const BirhausCard = forwardRef<HTMLDivElement, BirhausCardProps>(({
  // Content (Spanish-first)
  title,
  titleEs,
  titleEn,
  subtitle,
  subtitleEs,
  subtitleEn,
  description,
  descriptionEs,
  descriptionEn,
  
  // Layout
  children,
  header,
  footer,
  aside,
  
  // Actions
  primaryAction,
  secondaryActions = [],
  
  // Progressive disclosure
  collapsible = false,
  defaultExpanded = true,
  expandedContent,
  
  // Status
  status = 'neutral',
  statusMessage,
  statusMessageEs,
  statusMessageEn,
  
  // Appearance
  variant = 'default',
  size = 'md',
  
  // State
  loading = false,
  skeleton = false,
  lazyLoad = false,
  
  // Selection
  selectable = false,
  selected = false,
  onSelect,
  
  // Badge
  badge,
  badgeVariant = 'neutral',
  
  // Metadata
  timestamp,
  author,
  category,
  
  // Cognitive load
  informationDensity = 'medium',
  
  // Styling
  className,
  ...props
}, ref) => {
  
  // BIRHAUS: State management
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayTitle = resolveText(titleEs, titleEn, title)
  const displaySubtitle = resolveText(subtitleEs, subtitleEn, subtitle)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  const displayStatusMessage = resolveText(statusMessageEs, statusMessageEn, statusMessage)
  
  // BIRHAUS: Warn about cognitive overload
  const actionCount = (primaryAction ? 1 : 0) + secondaryActions.length
  if (actionCount > 4) {
    console.warn(`BIRHAUS 4-3-1 Rule Warning: Card has ${actionCount} actions (recommended: max 4)`)
  }
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  }
  
  const variantClasses = {
    default: 'bg-white border border-gray-200 shadow-sm',
    elevated: 'bg-white border border-gray-200 shadow-md hover:shadow-lg transition-shadow',
    outlined: 'bg-white border-2 border-gray-300',
    minimal: 'bg-transparent border-0'
  }
  
  const statusClasses = {
    neutral: '',
    success: 'border-green-200 bg-green-50',
    warning: 'border-yellow-200 bg-yellow-50',
    error: 'border-red-200 bg-red-50',
    info: 'border-blue-200 bg-blue-50'
  }
  
  const statusIcons = {
    neutral: null,
    success: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
    error: <AlertTriangle className="h-4 w-4 text-red-600" />,
    info: <Info className="h-4 w-4 text-blue-600" />
  }
  
  const badgeClasses = {
    neutral: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800'
  }
  
  const densityClasses = {
    low: 'space-y-6',
    medium: 'space-y-4',
    high: 'space-y-2'
  }
  
  const baseClasses = cn(
    'rounded-lg transition-all duration-200',
    safeAccess(sizeClasses, size, sizeClasses.md),
    safeAccess(variantClasses, variant, variantClasses.default),
    status !== 'neutral' && safeAccess(statusClasses, status, ''),
    selectable && 'cursor-pointer hover:border-blue-300',
    selected && 'ring-2 ring-blue-500 border-blue-300',
    informationDensity && safeAccess(densityClasses, informationDensity, densityClasses.medium),
    className
  )
  
  // BIRHAUS: Skeleton loading state
  if (skeleton || loading) {
    return (
      <div ref={ref} className={baseClasses} {...props}>
        <div className="animate-pulse space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div className="h-8 w-8 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
          </div>
          <div className="flex justify-end gap-2">
            <div className="h-8 w-16 bg-gray-200 rounded"></div>
            <div className="h-8 w-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div 
      ref={ref} 
      className={baseClasses}
      onClick={selectable ? onSelect : undefined}
      {...props}
    >
      {/* Custom header */}
      {header && (
        <div className="mb-4 pb-4 border-b border-gray-200">
          {header}
        </div>
      )}
      
      {/* Card header with title, subtitle, and actions */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          {/* Badge */}
          {badge && (
            <div className="mb-2">
              <span className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                safeAccess(badgeClasses, badgeVariant, badgeClasses.neutral)
              )}>
                {badge}
              </span>
            </div>
          )}
          
          {/* Title */}
          {displayTitle && (
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {displayTitle}
            </h3>
          )}
          
          {/* Subtitle */}
          {displaySubtitle && (
            <p className="text-sm text-gray-600 mt-1">
              {displaySubtitle}
            </p>
          )}
          
          {/* Metadata */}
          {(timestamp || author || category) && (
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              {timestamp && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{timestamp.toLocaleDateString('es-ES')}</span>
                </div>
              )}
              {author && <span>por {author}</span>}
              {category && (
                <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                  {category}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Actions dropdown or primary action */}
        <div className="flex items-center gap-2 ml-4">
          {/* BIRHAUS Principle #3: One clear primary action */}
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled || primaryAction.loading}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-50',
                primaryAction.variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
                primaryAction.variant === 'destructive' && 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
                (!primaryAction.variant || primaryAction.variant === 'secondary') && 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500'
              )}
            >
              {primaryAction.loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
              ) : primaryAction.icon}
              {primaryAction.labelEs || primaryAction.label}
            </button>
          )}
          
          {/* Secondary actions */}
          {secondaryActions.length > 0 && (
            <div className="relative">
              <button
                className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded"
                aria-label="Más acciones"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {/* Collapsible toggle */}
          {collapsible && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded"
              aria-label={isExpanded ? 'Contraer' : 'Expandir'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Status message */}
      {displayStatusMessage && (
        <div className={cn(
          'flex items-center gap-2 p-3 rounded-lg mb-4 text-sm',
          status === 'success' && 'bg-green-100 text-green-800',
          status === 'warning' && 'bg-yellow-100 text-yellow-800',
          status === 'error' && 'bg-red-100 text-red-800',
          status === 'info' && 'bg-blue-100 text-blue-800'
        )}>
          {safeAccess(statusIcons, status, statusIcons.info)}
          <span>{displayStatusMessage}</span>
        </div>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-gray-600 text-sm mb-4">
          {displayDescription}
        </p>
      )}
      
      {/* Aside content */}
      {aside && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          {aside}
        </div>
      )}
      
      {/* Main content */}
      {children && (
        <div className="mb-4">
          {children}
        </div>
      )}
      
      {/* Expanded content (progressive disclosure) */}
      {collapsible && expandedContent && isExpanded && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          {expandedContent}
        </div>
      )}
      
      {/* Custom footer */}
      {footer && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {footer}
        </div>
      )}
    </div>
  )
})

BirhausCard.displayName = 'BirhausCard'

// BIRHAUS: Hook for managing card state
export function useBirhausCard() {
  const [expanded, setExpanded] = useState(false)
  const [selected, setSelected] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const toggleExpanded = () => setExpanded(prev => !prev)
  const toggleSelected = () => setSelected(prev => !prev)
  
  return {
    expanded,
    selected,
    loading,
    setExpanded,
    setSelected,
    setLoading,
    toggleExpanded,
    toggleSelected
  }
}

// BIRHAUS: Helper to create card actions with Spanish-first labels
export function createBirhausCardAction(
  id: string,
  labelEs: string,
  onClick: () => void,
  options?: {
    labelEn?: string
    icon?: ReactNode
    variant?: 'primary' | 'secondary' | 'destructive'
    disabled?: boolean
  }
): BirhausCardAction {
  return {
    id,
    label: labelEs,
    labelEs,
    labelEn: options?.labelEn,
    onClick,
    icon: options?.icon,
    variant: options?.variant || 'secondary',
    disabled: options?.disabled || false,
    loading: false
  }
}

// Components and utilities are exported via individual export declarations above